import React, { useState, useEffect } from 'react'
import { APIURL } from '../components/Api.jsx';

import {
  MessageSquare,
  Search,
  Filter,
  Eye,
  Reply,
  Clock,
  User,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader2,
  Calendar,
  Tag,
  Mail,
  RefreshCw,
  HelpCircle,
  ArrowUpDown
} from 'lucide-react'
import { GetUserSummary } from '../components/Api.jsx'
import Pagination from '../components/Pagination.jsx'

const HelpSupportPage = () => {
  const [feedbacks, setFeedbacks] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedFeedback, setSelectedFeedback] = useState(null)
  const [showReplyModal, setShowReplyModal] = useState(false)
  const [replyText, setReplyText] = useState('')
  const [replying, setReplying] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [typeFilter, setTypeFilter] = useState('ALL')
  const [userDetails, setUserDetails] = useState({})
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    inProgress: 0,
    resolved: 0,
    closed: 0
  })
  const [refreshing, setRefreshing] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(6)
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' })



  const getToken = () => {
    const token = localStorage.getItem('accessToken')
    const tokenType = localStorage.getItem('tokenType') || 'Bearer'
    return token ? `${tokenType} ${token}` : null
  }

  const fetchFeedbacks = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true)
      } else {
        setLoading(true)
      }

      const token = getToken()

      const response = await fetch(`${APIURL}/api/v1/admin/feedback`, {
        method: 'GET',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        const feedbackList = data.data || []
        setFeedbacks(feedbackList)
        // Load user details for all feedback
        await loadUserDetails(feedbackList)
      } else {
        console.error('Failed to fetch feedbacks')
      }
    } catch (error) {
      console.error('Error fetching feedbacks:', error)
    } finally {
      if (isRefresh) {
        setRefreshing(false)
      } else {
        setLoading(false)
      }
    }
  }

  const handleRefresh = () => {
    fetchFeedbacks(true)
    fetchStats()
  }

  const fetchStats = async () => {
    try {
      const token = getToken()

      const response = await fetch(`${APIURL}/api/v1/admin/feedback/stats`, {
        method: 'GET',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setStats(data.data || {})
      }
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }

  const fetchUserDetails = async (userId) => {
    try {
      const response = await GetUserSummary(userId)
      if (response.data && response.data.data) {
        const userData = response.data.data
        return {
          firstName: userData.firstName || 'N/A',
          lastName: userData.lastName || 'N/A',
          email: userData.email || 'N/A',
          fullName: `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'N/A'
        }
      }
    } catch (error) {
      console.error(`Error fetching user details for ${userId}:`, error)
    }
    return {
      firstName: 'N/A',
      lastName: 'N/A',
      email: 'N/A',
      fullName: 'N/A'
    }
  }

  const loadUserDetails = async (feedbackList) => {
    const uniqueUserIds = [...new Set(feedbackList.map(f => f.userId))]
    const userDetailsMap = {}

    for (const userId of uniqueUserIds) {
      if (!userDetails[userId]) {
        userDetailsMap[userId] = await fetchUserDetails(userId)
      }
    }

    setUserDetails(prev => ({ ...prev, ...userDetailsMap }))
  }

  const handleReply = async () => {
    if (!selectedFeedback || !replyText.trim()) return

    try {
      setReplying(true)
      const token = getToken()

      const response = await fetch(`${APIURL}/api/v1/admin/feedback/${selectedFeedback.id}/response`, {
        method: 'PUT',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          adminResponse: replyText
        })
      })

      if (response.ok) {
        const data = await response.json()
        // Update the feedback in the list - set status to IN_PROGRESS when admin replies
        setFeedbacks(prev => prev.map(f =>
          f.id === selectedFeedback.id
            ? { ...f, adminResponse: replyText, status: 'IN_PROGRESS' }
            : f
        ))
        setShowReplyModal(false)
        setReplyText('')
        setSelectedFeedback(null)
        fetchStats() // Refresh stats
      } else {
        console.error('Failed to send reply')
      }
    } catch (error) {
      console.error('Error sending reply:', error)
    } finally {
      setReplying(false)
    }
  }

  const updateStatus = async (feedbackId, newStatus) => {
    try {
      const token = getToken()

      const response = await fetch(`${APIURL}/api/v1/admin/feedback/${feedbackId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus
        })
      })

      if (response.ok) {
        // Update the feedback in the list
        setFeedbacks(prev => prev.map(f =>
          f.id === feedbackId
            ? { ...f, status: newStatus }
            : f
        ))
        fetchStats() // Refresh stats
      } else {
        console.error('Failed to update status')
      }
    } catch (error) {
      console.error('Error updating status:', error)
    }
  }

  useEffect(() => {
    fetchFeedbacks()
    fetchStats()
  }, [])

  const getStatusIcon = (status) => {
    switch (status) {
      case 'OPEN':
        return <AlertCircle className="w-4 h-4 text-red-500" />
      case 'IN_PROGRESS':
        return <Clock className="w-4 h-4 text-yellow-500" />
      case 'RESOLVED':
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'CLOSED':
        return <XCircle className="w-4 h-4 text-gray-500" />
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'OPEN':
        return 'bg-red-100 text-red-800'
      case 'IN_PROGRESS':
        return 'bg-yellow-100 text-yellow-800'
      case 'RESOLVED':
        return 'bg-green-100 text-green-800'
      case 'CLOSED':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getTypeColor = (type) => {
    switch (type) {
      case 'SUPPORT':
        return 'bg-blue-100 text-blue-800'
      case 'BUG':
        return 'bg-red-100 text-red-800'
      case 'FEATURE':
        return 'bg-purple-100 text-purple-800'
      case 'FEEDBACK':
        return 'bg-green-100 text-green-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const filteredFeedbacks = (() => {
    let filtered = feedbacks.filter(feedback => {
      const userInfo = userDetails[feedback.userId]
      const userName = userInfo?.fullName || feedback.userId
      const userEmail = userInfo?.email || ''

      const matchesSearch = feedback.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        feedback.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        userEmail.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesStatus = statusFilter === 'ALL' || feedback.status === statusFilter
      const matchesType = typeFilter === 'ALL' || feedback.type === typeFilter
      return matchesSearch && matchesStatus && matchesType
    });

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue, bValue;
        if (sortConfig.key === 'user') {
          const aUser = userDetails[a.userId]?.fullName || a.userId || '';
          const bUser = userDetails[b.userId]?.fullName || b.userId || '';
          aValue = String(aUser).toLowerCase();
          bValue = String(bUser).toLowerCase();
        } else if (sortConfig.key === 'date') {
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
        } else {
          aValue = String(a[sortConfig.key] || '').toLowerCase();
          bValue = String(b[sortConfig.key] || '').toLowerCase();
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  })();

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? '↑' : '↓';
    }
    return '';
  };

  // Calculate paginated feedbacks
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedFeedbacks = filteredFeedbacks.slice(indexOfFirstItem, indexOfLastItem);

  const canReply = (feedback) => {
    return feedback.type === 'SUPPORT' && feedback.status !== 'CLOSED'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">Loading feedback...</span>
      </div>
    )
  }

  return (
    <div className="p-4">
      {/* Header */}
      <div className="mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col lg:flex-row justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-start gap-4">
                <div className="bg-purple-100 p-3 rounded-lg">
                  <HelpCircle className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-3xl">
                    Manage user feedback and support tickets. Respond to user inquiries, track support requests, and maintain customer satisfaction through effective communication.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg transition-all duration-200 disabled:cursor-not-allowed text-sm font-medium shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                aria-label={refreshing ? "Refreshing data" : "Refresh help & support"}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards - Compact */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <div className="bg-white p-3 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total</p>
              <p className="text-lg font-bold text-gray-900">{stats.total}</p>
            </div>
            <MessageSquare className="w-5 h-5 text-blue-500" />
          </div>
        </div>
        <div className="bg-white p-3 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Open</p>
              <p className="text-lg font-bold text-red-600">{stats.open}</p>
            </div>
            <AlertCircle className="w-5 h-5 text-red-500" />
          </div>
        </div>
        <div className="bg-white p-3 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">In Progress</p>
              <p className="text-lg font-bold text-yellow-600">{stats.inProgress}</p>
            </div>
            <Clock className="w-5 h-5 text-yellow-500" />
          </div>
        </div>
        <div className="bg-white p-3 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Resolved</p>
              <p className="text-lg font-bold text-green-600">{stats.resolved}</p>
            </div>
            <CheckCircle className="w-5 h-5 text-green-500" />
          </div>
        </div>
        <div className="bg-white p-3 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Closed</p>
              <p className="text-lg font-bold text-gray-600">{stats.closed}</p>
            </div>
            <XCircle className="w-5 h-5 text-gray-500" />
          </div>
        </div>
      </div>

      {/* Filters - Compact */}
      <div className="bg-white p-3 rounded-lg border border-gray-200 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search feedback, user name, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-3 py-2 text-sm text-gray-900 placeholder-gray-500 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-2 py-2 text-sm text-gray-900 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="ALL">All Status</option>
              <option value="OPEN">Open</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="RESOLVED">Resolved</option>
              <option value="CLOSED">Closed</option>
            </select>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-2 py-2 text-sm text-gray-900 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="ALL">All Types</option>
              <option value="SUPPORT">Support</option>
              <option value="BUG">Bug</option>
              <option value="FEATURE">Feature</option>
              <option value="FEEDBACK">Feedback</option>
            </select>
          </div>
        </div>
      </div>

      {/* Feedback List - Compact */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  S.No
                </th>
                <th
                  className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('user')}
                >
                  <div className="flex items-center">
                    User
                    <ArrowUpDown className="ml-1 w-4 h-4" />
                    <span className="ml-1">{getSortIndicator('user')}</span>
                  </div>
                </th>
                <th
                  className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('subject')}
                >
                  <div className="flex items-center">
                    Subject
                    <ArrowUpDown className="ml-1 w-4 h-4" />
                    <span className="ml-1">{getSortIndicator('subject')}</span>
                  </div>
                </th>
                <th
                  className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('type')}
                >
                  <div className="flex items-center">
                    Type
                    <ArrowUpDown className="ml-1 w-4 h-4" />
                    <span className="ml-1">{getSortIndicator('type')}</span>
                  </div>
                </th>
                <th
                  className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center">
                    Status
                    <ArrowUpDown className="ml-1 w-4 h-4" />
                    <span className="ml-1">{getSortIndicator('status')}</span>
                  </div>
                </th>
                <th
                  className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('date')}
                >
                  <div className="flex items-center">
                    Date
                    <ArrowUpDown className="ml-1 w-4 h-4" />
                    <span className="ml-1">{getSortIndicator('date')}</span>
                  </div>
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {paginatedFeedbacks.map((feedback, index) => (
                <tr key={feedback.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-sm text-gray-500 font-mono">
                    {(currentPage - 1) * itemsPerPage + index + 1}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                        <User className="w-4 h-4 text-gray-500" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {userDetails[feedback.userId]?.fullName || feedback.userId}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {userDetails[feedback.userId]?.email || 'Loading...'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="max-w-xs">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {feedback.subject}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {feedback.message}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(feedback.type)}`}>
                      {feedback.type}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      {getStatusIcon(feedback.status)}
                      <span className={`ml-1.5 inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(feedback.status)}`}>
                        {feedback.status.replace('_', ' ')}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {new Date(feedback.createdAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => setSelectedFeedback(feedback)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="View details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {canReply(feedback) && (
                        <button
                          onClick={() => {
                            setSelectedFeedback(feedback)
                            setShowReplyModal(true)
                          }}
                          className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                          title="Reply"
                        >
                          <Reply className="w-4 h-4" />
                        </button>
                      )}
                      {/* Status Management Buttons */}
                      {feedback.status === 'OPEN' && (
                        <button
                          onClick={() => updateStatus(feedback.id, 'IN_PROGRESS')}
                          className="p-1.5 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 rounded transition-colors"
                          title="Mark as In Progress"
                        >
                          <Clock className="w-4 h-4" />
                        </button>
                      )}
                      {feedback.status === 'IN_PROGRESS' && (
                        <button
                          onClick={() => updateStatus(feedback.id, 'RESOLVED')}
                          className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                          title="Mark as Resolved"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      )}
                      {feedback.status === 'RESOLVED' && (
                        <button
                          onClick={() => updateStatus(feedback.id, 'CLOSED')}
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded transition-colors"
                          title="Close Ticket"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredFeedbacks.length === 0 && (
          <div className="text-center py-12">
            <MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No feedback found</p>
            <p className="text-xs text-gray-400 mt-1">Try adjusting your search or filters</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {filteredFeedbacks.length > 0 && (
        <Pagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={Math.ceil(filteredFeedbacks.length / itemsPerPage)}
          totalElements={filteredFeedbacks.length}
          pageSize={itemsPerPage}
          setPageSize={setItemsPerPage}
          itemName="Tickets"
        />
      )}

      {/* View Feedback Modal - Compact */}
      {selectedFeedback && !showReplyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Feedback Details</h2>
              <button
                onClick={() => setSelectedFeedback(null)}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">

              <div className="space-y-4">
                {/* User Info */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center mr-3">
                      <User className="w-5 h-5 text-gray-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {userDetails[selectedFeedback.userId]?.fullName || selectedFeedback.userId}
                      </p>
                      <p className="text-xs text-gray-500">
                        {userDetails[selectedFeedback.userId]?.email || 'Loading...'}
                      </p>
                      <p className="text-xs text-gray-400">
                        ID: {selectedFeedback.userId}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Status & Type */}
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Type</label>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(selectedFeedback.type)}`}>
                      {selectedFeedback.type}
                    </span>
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Status</label>
                    <div className="flex items-center">
                      {getStatusIcon(selectedFeedback.status)}
                      <span className={`ml-1.5 inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(selectedFeedback.status)}`}>
                        {selectedFeedback.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Subject</label>
                  <p className="text-sm text-gray-900 font-medium">{selectedFeedback.subject}</p>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Message</label>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedFeedback.message}</p>
                  </div>
                </div>

                {/* Admin Response */}
                {selectedFeedback.adminResponse && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Admin Response</label>
                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedFeedback.adminResponse}</p>
                    </div>
                  </div>
                )}

                {/* Timestamps */}
                <div className="grid grid-cols-2 gap-3 text-xs text-gray-500">
                  <div>
                    <span className="font-medium">Created:</span> {new Date(selectedFeedback.createdAt).toLocaleString()}
                  </div>
                  {selectedFeedback.updatedAt && (
                    <div>
                      <span className="font-medium">Updated:</span> {new Date(selectedFeedback.updatedAt).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-between items-center">
                {/* Status Management Buttons */}
                <div className="flex space-x-2">
                  {selectedFeedback.status === 'OPEN' && (
                    <button
                      onClick={() => {
                        updateStatus(selectedFeedback.id, 'IN_PROGRESS')
                        setSelectedFeedback(null)
                      }}
                      className="px-3 py-2 bg-yellow-600 text-white text-sm rounded-md hover:bg-yellow-700 flex items-center"
                    >
                      <Clock className="w-4 h-4 mr-1.5" />
                      Start Progress
                    </button>
                  )}
                  {selectedFeedback.status === 'IN_PROGRESS' && (
                    <button
                      onClick={() => {
                        updateStatus(selectedFeedback.id, 'RESOLVED')
                        setSelectedFeedback(null)
                      }}
                      className="px-3 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 flex items-center"
                    >
                      <CheckCircle className="w-4 h-4 mr-1.5" />
                      Mark Resolved
                    </button>
                  )}
                  {selectedFeedback.status === 'RESOLVED' && (
                    <button
                      onClick={() => {
                        updateStatus(selectedFeedback.id, 'CLOSED')
                        setSelectedFeedback(null)
                      }}
                      className="px-3 py-2 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700 flex items-center"
                    >
                      <XCircle className="w-4 h-4 mr-1.5" />
                      Close Ticket
                    </button>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-2">
                  {canReply(selectedFeedback) && (
                    <button
                      onClick={() => setShowReplyModal(true)}
                      className="px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 flex items-center"
                    >
                      <Reply className="w-4 h-4 mr-1.5" />
                      Reply
                    </button>
                  )}
                  <button
                    onClick={() => setSelectedFeedback(null)}
                    className="px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reply Modal - Compact */}
      {showReplyModal && selectedFeedback && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Reply to Support Ticket</h2>
              <button
                onClick={() => {
                  setShowReplyModal(false)
                  setReplyText('')
                }}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4">
              {/* Ticket Info */}
              <div className="bg-gray-50 p-3 rounded-lg mb-4">
                <p className="text-sm font-medium text-gray-900 mb-1">
                  {selectedFeedback.subject}
                </p>
                <p className="text-xs text-gray-600">
                  <strong>User:</strong> {userDetails[selectedFeedback.userId]?.fullName || selectedFeedback.userId}
                </p>
                <p className="text-xs text-gray-600">
                  <strong>Email:</strong> {userDetails[selectedFeedback.userId]?.email || 'Loading...'}
                </p>
              </div>

              {/* Reply Form */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Response
                </label>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  rows={5}
                  className="w-full px-3 py-2 text-sm text-gray-900 placeholder-gray-500 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Type your response here..."
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => {
                    setShowReplyModal(false)
                    setReplyText('')
                  }}
                  className="px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300"
                  disabled={replying}
                >
                  Cancel
                </button>
                <button
                  onClick={handleReply}
                  disabled={!replyText.trim() || replying}
                  className="px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {replying ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-1.5" />
                      Send Reply
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default HelpSupportPage
